import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Shipment } from '@stone/interfaces';

interface ShipmentsState {
  items: Shipment[];
  totalCount: number;
  loading: boolean;
  currentShipment: {
    data: Shipment | null;
    loading: boolean;
  };
  saving: boolean;
  error: string | null;
}

const initialState: ShipmentsState = {
  items: [],
  totalCount: 0,
  loading: false,
  currentShipment: {
    data: null,
    loading: false,
  },
  saving: false,
  error: null,
};

const shipmentsSlice = createSlice({
  name: 'stone.shipments',
  initialState,
  reducers: {
    fetchShipmentsRequest: (state, _action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    fetchShipmentsSuccess: (state, action: PayloadAction<{ items: Shipment[]; totalCount: number }>) => {
      state.items = action.payload.items;
      state.totalCount = action.payload.totalCount;
      state.loading = false;
    },
    fetchShipmentsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchShipmentByIdRequest: (state, _action: PayloadAction<number | string>) => {
      state.currentShipment.loading = true;
      state.currentShipment.data = null;
      state.error = null;
    },
    fetchShipmentByIdSuccess: (state, action: PayloadAction<Shipment>) => {
      state.currentShipment.data = action.payload;
      state.currentShipment.loading = false;
    },
    fetchShipmentByIdFailure: (state, action: PayloadAction<string>) => {
      state.currentShipment.loading = false;
      state.error = action.payload;
    },
    saveShipmentRequest: (state, _action: PayloadAction<Partial<Shipment>>) => {
      state.saving = true;
      state.error = null;
    },
    saveShipmentSuccess: (state, action: PayloadAction<Shipment>) => {
      state.saving = false;
      state.currentShipment.data = action.payload;
    },
    saveShipmentFailure: (state, action: PayloadAction<string>) => {
      state.saving = false;
      state.error = action.payload;
    },
    shipShipmentRequest: (state, _action: PayloadAction<Partial<Shipment>>) => {
      state.saving = true;
      state.error = null;
    },
    shipShipmentSuccess: (state, action: PayloadAction<Shipment>) => {
      state.saving = false;
      state.currentShipment.data = action.payload;
    },
    shipShipmentFailure: (state, action: PayloadAction<string>) => {
      state.saving = false;
      state.error = action.payload;
    },
    /**
     * Resets `currentShipment.data` and signals a new load is starting via
     * `loading: true`. Used by the Receive flow's barcode scan so any other
     * page reading `currentShipment.loading` (e.g. ViewShipment) sees the
     * load-in-progress state instead of stale data.
     */
    clearCurrentShipment: (state) => {
      state.currentShipment.data = null;
      state.currentShipment.loading = true;
    },
  },
});

export const {
  fetchShipmentsRequest,
  fetchShipmentsSuccess,
  fetchShipmentsFailure,
  fetchShipmentByIdRequest,
  fetchShipmentByIdSuccess,
  fetchShipmentByIdFailure,
  saveShipmentRequest,
  saveShipmentSuccess,
  saveShipmentFailure,
  shipShipmentRequest,
  shipShipmentSuccess,
  shipShipmentFailure,
  clearCurrentShipment,
} = shipmentsSlice.actions;
export default shipmentsSlice.reducer;
